/**
 * P1.4f — Compass scenario projector.
 *
 * Mirrors `networkScenarioProjector` + `processScenarioProjector`,
 * adapted for the Compass Explorer scene (bubbles, container frames,
 * and leaves at L3+).
 *
 * Pure function. Takes the base scene + a `ScenarioDelta` and returns
 * a projected scene with:
 *  - phantom nodes appended for each `addition` (stamped
 *    `scenarioStatus: 'added'`)
 *  - removed nodes retained with `scenarioStatus: 'removed'` so the
 *    user can see what's going away
 *  - update targets re-stamped with `scenarioStatus: 'changed'`
 *  - phantom edges appended for `edgeAdditions`
 *
 * Federation-aware attribution: each addition carries
 * `props.sourceFolderId` identifying the federated source folder the
 * phantom is targeting. `applyCompassScenario` groups by that field
 * when emitting change-sets so the read-only federation rule is
 * preserved.
 */

import type { Node, Edge } from '../../../core/models/scene';
import type {
  ScenarioDelta,
  NodeAddition,
  EdgeAddition,
} from '../../../core/data-profiles/scenarios/ScenarioDelta';
import { buildStatusIndex } from '../../../core/data-profiles/scenarios/ScenarioModel';
import { getShapeManifest } from '../../../lib/diagram/registries';

const COMPASS_CATEGORY = 'network'; // Compass Explorer reuses the network diagram type.

/**
 * Default bubble + container sizes when a manifest lookup misses.
 * Keeps phantom nodes laid out roughly like real scanned resources.
 */
const DEFAULT_BUBBLE_SIZE = { width: 220, height: 160 };
const DEFAULT_LEAF_SIZE = { width: 180, height: 60 };

const resolveDefaultDimensions = (
  shapeType: string,
): { width: number; height: number } => {
  const manifest = getShapeManifest(COMPASS_CATEGORY as any, shapeType);
  if (manifest?.defaultDimensions) return manifest.defaultDimensions;
  return shapeType === 'compass_bubble' ? DEFAULT_BUBBLE_SIZE : DEFAULT_LEAF_SIZE;
};

const resolveDefaultProps = (shapeType: string): Record<string, unknown> => {
  const manifest = getShapeManifest(COMPASS_CATEGORY as any, shapeType);
  if (!manifest?.defaultProps) return {};
  try {
    return typeof manifest.defaultProps === 'function'
      ? (manifest.defaultProps as () => Record<string, unknown>)()
      : (manifest.defaultProps as Record<string, unknown>);
  } catch {
    return {};
  }
};

const additionToNode = (addition: NodeAddition): Node => {
  const dims = resolveDefaultDimensions(addition.type);
  const defaults = resolveDefaultProps(addition.type);
  const label = addition.name || addition.title || 'Proposed resource';
  const attrs: Record<string, unknown> = {
    ...defaults,
    ...(addition.props ?? {}),
    name: label,
    label,
    scenarioStatus: 'added',
    // Legacy `provisional` flag — kept so renderers that already
    // branch on it (see `CompassContainerCard`) light up without a
    // parallel code path. `scenarioStatus` is the canonical signal.
    provisional: true,
  };

  return {
    id: addition.id as string,
    type: addition.type,
    category: COMPASS_CATEGORY,
    x: addition.position?.x ?? 0,
    y: addition.position?.y ?? 0,
    w: dims.width,
    h: dims.height,
    width: dims.width,
    height: dims.height,
    text: label,
    attrs,
    props: {
      ...defaults,
      ...(addition.props ?? {}),
    },
  } as unknown as Node;
};

const additionToEdge = (addition: EdgeAddition): Edge => {
  return {
    id: addition.id as string,
    type: addition.type as Edge['type'],
    from: addition.from as string,
    to: addition.to as string,
    attrs: {
      scenarioStatus: 'added',
      provisional: true,
      ...(addition.props ?? {}),
    },
  } as unknown as Edge;
};

/**
 * Apply `delta` to `baseNodes` + `baseEdges`. Returns the projected
 * scene plus a status index (consumed by the scenarios sub-panel and
 * the control-impact service).
 */
export const projectCompassScenario = (
  baseNodes: Node[],
  baseEdges: Edge[],
  delta: ScenarioDelta<any, any>,
): {
  nodes: Node[];
  edges: Edge[];
  statusIndex: ReturnType<typeof buildStatusIndex>;
} => {
  if (isDeltaEmpty(delta)) {
    return {
      nodes: baseNodes,
      edges: baseEdges,
      statusIndex: buildStatusIndex(delta),
    };
  }

  const removalIds = new Set(delta.removals.map((r) => r.nodeId as string));
  const edgeRemovalIds = new Set(
    delta.edgeRemovals.map((r) => r.edgeId as string),
  );
  const updateIndex = new Map(delta.updates.map((u) => [u.nodeId as string, u]));

  const projectedNodes: Node[] = baseNodes
    .map((node) => {
      if (removalIds.has(node.id)) {
        return {
          ...node,
          attrs: {
            ...(node.attrs as Record<string, unknown>),
            scenarioStatus: 'removed',
          },
        } as Node;
      }

      const update = updateIndex.get(node.id);
      if (!update) return node;

      const { field, newValue } = update.change as {
        field: string;
        newValue: unknown;
      };
      return {
        ...node,
        [field]: newValue,
        attrs: {
          ...(node.attrs as Record<string, unknown>),
          [field]: newValue,
          scenarioStatus: 'changed',
        },
        props: {
          ...(node.props as Record<string, unknown>),
          [field]: newValue,
        },
      } as Node;
    })
    .concat(delta.additions.map(additionToNode));

  const projectedEdges: Edge[] = baseEdges
    .map((edge) => {
      if (
        edgeRemovalIds.has(edge.id) ||
        removalIds.has(edge.from) ||
        removalIds.has(edge.to)
      ) {
        return {
          ...edge,
          attrs: {
            ...(edge.attrs as Record<string, unknown>),
            scenarioStatus: 'removed',
          },
        } as Edge;
      }
      return edge;
    })
    .concat(delta.edgeAdditions.map(additionToEdge));

  return {
    nodes: projectedNodes,
    edges: projectedEdges,
    statusIndex: buildStatusIndex(delta),
  };
};

function isDeltaEmpty(delta: ScenarioDelta<any, any>): boolean {
  return (
    delta.additions.length === 0 &&
    delta.removals.length === 0 &&
    delta.moves.length === 0 &&
    delta.updates.length === 0 &&
    delta.edgeAdditions.length === 0 &&
    delta.edgeRemovals.length === 0
  );
}
